// analytics/hooks/useDermatologistActivity.ts — SKIN-39
import { useState, useEffect } from 'react';
import axios from 'axios';
import { clinicalServices } from '../services/endpoints/clinical';
import { DermatologistActivityRow } from '../ActivityPerDermatologist';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// Volume metrics are range-bounded; if the page hasn't picked a range yet we
// default to the last 30 days (per SKIN-39). last-active is range-independent
// server-side, so it always reflects the most recent activity regardless.
export const useDermatologistActivity = (
    investigation: { uuid: string } | undefined,
    startDate: number | null,
    endDate: number | null,
) => {
    const [data, setData] = useState<DermatologistActivityRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    const retry = () => setReloadToken(t => t + 1);

    useEffect(() => {
        // One mechanism does both jobs here. Cancelling actually aborts the XHR (axios
        // 0.21 honours cancelToken, not AbortSignal — see the note in the service), and
        // `token.reason`, which `cancel()` sets synchronously, marks this effect run as
        // superseded so a late response cannot write over newer state.
        const source = axios.CancelToken.source();
        const superseded = () => Boolean(source.token.reason);

        const fetchData = async () => {
            if (!investigation?.uuid) return;
            const end = endDate ?? Date.now();
            const start = startDate ?? end - THIRTY_DAYS_MS;
            setLoading(true);
            setError(null);
            try {
                const rows = await clinicalServices.getDermatologistActivity(investigation.uuid, start, end, source.token);
                // Guarding this was the actual bug: without it, a response for a range
                // the user has already moved off overwrites the newer rows, and the
                // table silently disagrees with the date picker above it.
                if (superseded()) return;
                setData(rows);
            } catch (err) {
                // A cancellation is not a failure — painting an error state for a request
                // we ourselves discarded would report a broken dashboard to the user.
                if (axios.isCancel(err) || superseded()) return;
                setError(err instanceof Error ? err : new Error('Unknown error'));
            } finally {
                if (!superseded()) setLoading(false);
            }
        };

        fetchData();
        return () => source.cancel('superseded');
    }, [investigation?.uuid, startDate, endDate, reloadToken]);

    return { data, loading, error, retry };
};
